import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from "axios";
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Star, ArrowLeft, Info, Bus, UtensilsCrossed, X } from 'lucide-react';
import { useFloorManager } from '../context/FloorManagerContext';

const SuperVisorRating = () => {
  const navigate = useNavigate();
  const { token } = useFloorManager();
  const [loading, setLoading] = useState(true);
  const [workerData, setWorkerData] = useState({});
  const [ratingHistory, setRatingHistory] = useState([]);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isTooltipPinned, setIsTooltipPinned] = useState(false);
  const [error, setError] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState('');
  // Rating Modal States
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewData, setReviewData] = useState({
    id: null,
    stars: 0,
    discipline: '',
    qualityOfWork: '',
    behaviour: '',
    isEditing: false,
    profilePhotoUrl: null
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  
  // Debug logs
  const urlPath = window.location.pathname;
  const pathParts = urlPath.split('/');
  const userId = pathParts[pathParts.length - 1];
  console.log('Component rendered with userId:', userId);
  console.log('BASE_URL:', BASE_URL);
  
  // Create ref for the tooltip
  const tooltipRef = useRef(null);

  useEffect(() => {
  if (userId && token) {
    fetchRatingHistory();
  }
}, [userId, token, fetchRatingHistory]);
  
  // Effect to handle clicks outside the tooltip
  useEffect(() => {
    const handleClickOutside = (event) => {
      // If tooltip is pinned and visible, and click is outside the tooltip
      if (isTooltipPinned && tooltipVisible && tooltipRef.current && 
          !tooltipRef.current.contains(event.target)) {
        setIsTooltipPinned(false);
        setTooltipVisible(false);
      }
    };

    // Add event listener when component mounts
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up event listener when component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTooltipPinned, tooltipVisible]);

  // Helper function to calculate working hours
  const calculateWorkingHours = (entryDate, exitDate) => {
    if (!entryDate || !exitDate) return null;
    
    try {
      const entry = new Date(entryDate);
      const exit = new Date(exitDate);
      const diffMs = exit - entry;
      const diffHours = diffMs / (1000 * 60 * 60);
      return Math.round(diffHours * 100) / 100; // Round to 2 decimal places
    } catch (error) {
      console.error('Error calculating working hours:', error);
      return null;
    }
  };
  
  const fetchRatingHistory = useCallback(async () => {
    console.log('fetchRatingHistory called with userId:', userId);
    
    if (!userId) {
      console.error('No userId provided');
      setError('No user ID provided');
      setLoading(false);
      return;
    }

    if (!BASE_URL) {
      console.error('BASE_URL not configured');
      setError('Base URL not configured');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const department = localStorage.getItem("department");
      
      console.log('Making API call with:', {
        userId,
        department,
        hasToken: !!token,
        baseUrl: BASE_URL
      });
      
      const response = await axios.post(`${BASE_URL}/rating-lenskart/getByWorkerId`, {
        userId: userId,
        department: department
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('API Response:', response.data);
      
      // Update worker data based on the actual API response structure
      setWorkerData({
        name: response.data.name,
        phone: response.data.phone,
        profilePhotoUrl: response.data.profilePhotoUrl || null
      });
      
      // Transform the attendance data to match the expected structure
      const transformedData = response.data.attendance.map(record => ({
        id: record._id,
        entryDateTime: record.EntryDate,
        exitDateTime: record.ExitDate,
        workingHours: calculateWorkingHours(record.EntryDate, record.ExitDate),
        rating: record.rating || null,
        comments: record.comment || [],
        createdAt: record.createdAt,
        busEntry: record.busEntry || false,
        busExit: record.busExit || false,
        haveFood: record.haveFood || false
      }));
      
      setRatingHistory(transformedData);
    } catch (error) {
      console.error('Error fetching rating history:', error);
      setError(`Failed to fetch rating history`);
    } finally {
      setLoading(false);
    }
  }, [userId, BASE_URL]);

  const formatDateTime = (dateTimeString, showIcons = false, busEntry = false, busExit = false, haveFood = false) => {
    if (!dateTimeString) return '-';
  
    try {
      const [datePart, timePartFull] = dateTimeString.split('T');
      if (!timePartFull) return '-';
  
      const timePart = timePartFull.slice(0, 5);
      let [hours, minutes] = timePart.split(':').map(Number);
  
      const ampm = hours >= 12 ? 'pm' : 'am';
      const hour12 = hours % 12 || 12;
      const formattedTime = `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  
      const date = new Date(datePart);
      const formattedDate = date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
  
      return (
        <div className="text-center">
          <div className="flex items-center justify-center gap-1">
            {formattedDate}
            {showIcons && busEntry && (
              <Bus
                className="h-4 w-4 text-[#3D5A80]"
                title="Used bus service for entry"
              />
            )}
            {showIcons && busExit && (
              <Bus 
                className="h-4 w-4 text-[#3D5A80]" 
                title="Used bus service for exit" 
              />
            )}
          </div>
          <div className="flex items-center justify-center gap-1 text-xs text-gray-500">
            {formattedTime}
            {showIcons && haveFood && (
              <UtensilsCrossed 
                className="h-3 w-3 text-[#F4A261]" 
                title="Availed food service" 
              />
            )}
          </div>
        </div>
      );
    } catch (e) {
      console.error('Error formatting date/time:', e);
      return '-';
    }
  };

  const calculateTooltipPosition = (rect) => {
    const tooltipWidth = 200; // Estimated tooltip width
    const viewportWidth = window.innerWidth;
    const padding = 16; // Padding from screen edges
    
    let left = rect.left + rect.width / 2;
    
    // Check if tooltip would go off the right edge
    if (left + tooltipWidth / 2 > viewportWidth - padding) {
      left = viewportWidth - tooltipWidth / 2 - padding;
    }
    
    // Check if tooltip would go off the left edge
    if (left - tooltipWidth / 2 < padding) {
      left = tooltipWidth / 2 + padding;
    }
    
    return {
      x: left,
      y: rect.top
    };
  };

  const handleInfoHover = (e, comments) => {
    if (!comments || comments.length === 0) return;
    
    const rect = e.target.getBoundingClientRect();
    const position = calculateTooltipPosition(rect);
    setTooltipPosition(position);
    
    // Format comments for display - each comment object as a separate line
    const formattedComments = comments.map(comment => {
      const key = Object.keys(comment)[0];
      const value = comment[key];
      return {key, value};
    });
    
    setTooltipContent(formattedComments);
    setTooltipVisible(true);
  };

  const handleInfoLeave = () => {
    if (!isTooltipPinned) {
      setTooltipVisible(false);
    }
  };

  const handleInfoClick = (e, comments) => {
    if (!comments || comments.length === 0) return;
    
    const rect = e.target.getBoundingClientRect();
    const position = calculateTooltipPosition(rect);
    setTooltipPosition(position);
    
    // Format comments for display - each comment object as a separate line
    const formattedComments = comments.map(comment => {
      const key = Object.keys(comment)[0];
      const value = comment[key];
      return {key, value};
    });
    
    setTooltipContent(formattedComments);
    setIsTooltipPinned(!isTooltipPinned);
    setTooltipVisible(!isTooltipPinned);
  };

  // Rating Modal Functions
  const getCommentValue = (comments, field) => {
    if (!comments || !Array.isArray(comments)) return '';
    const commentObj = comments.find(comment => comment[field]);
    return commentObj ? commentObj[field] : '';
  };

  const openReviewModal = (id, isEditing = false) => {
    const recordToEdit = isEditing ? 
      ratingHistory.find(record => record.id === id) : 
      null;
    
    const currentRecord = ratingHistory.find(record => record.id === id);
    
    setReviewData({
      id,
      stars: isEditing && recordToEdit.rating ? recordToEdit.rating : 0,
      discipline: isEditing && recordToEdit.comments ? getCommentValue(recordToEdit.comments, 'Discipline') : '',
      qualityOfWork: isEditing && recordToEdit.comments ? getCommentValue(recordToEdit.comments, 'Quality of Work') : '',
      behaviour: isEditing && recordToEdit.comments ? getCommentValue(recordToEdit.comments, 'Behaviour') : '',
      isEditing,
      profilePhotoUrl: null // Don't show photo in modal as requested
    });
    
    setShowReviewModal(true);
  };

  const handleStarClick = (rating) => {
    setReviewData({
      ...reviewData,
      stars: rating,
    });
  };

  const handleInputChange = (e, field) => {
    setReviewData({
      ...reviewData,
      [field]: e.target.value,
    });
  };

  const submitReview = async () => {
    try {
      let comment = [];
      
      if (reviewData.stars <= 3) {
        if (reviewData.discipline) {
          comment.push({ "Discipline": reviewData.discipline });
        }
        if (reviewData.qualityOfWork) {
          comment.push({ "Quality of Work": reviewData.qualityOfWork });
        }
        if (reviewData.behaviour) {
          comment.push({ "Behaviour": reviewData.behaviour });
        }
      }
      
      const requestData = {
        shiftId: reviewData.id,
        stars: reviewData.stars,
        ...(comment.length > 0 && { comment })
      };
      
      const endpoint = `${BASE_URL}/rating-lenskart/add`;
      
      await axios.post(endpoint, requestData, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      
      // Update the local state to reflect the new rating
      setRatingHistory(prev =>
        prev.map(record =>
          record.id === reviewData.id
            ? {
                ...record,
                rating: reviewData.stars,
                comments: comment
              }
            : record
        )
      );
      
      setShowReviewModal(false);
      setShowSuccessModal(true);
      
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 1000);
    } catch (error) {
      console.error('Error submitting review:', error);
      setErrorMessage('Failed to submit review. Please try again.');
      setShowErrorModal(true);
    }
  };

  const handlePhotoClick = (photoUrl) => {
    setSelectedPhoto(photoUrl);
    setShowPhotoModal(true);
  };

  const closePhotoModal = () => {
    setShowPhotoModal(false);
    setSelectedPhoto('');
  };

  return (
    <div className="min-h-screen overflow-y-auto pt-16 bg-gray-100 p-0">
      <div className="max-w-6xl mx-auto bg-white shadow-lg rounded-lg p-4">
        <div className="flex items-center justify-center mb-4">
          <h2 className="text-2xl font-bold">Worker Rating History</h2>
        </div>
        
    
          {error && (
            <div className="mb-4 p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-red-800">{error}</p>
            </div>
          )}
          
          {workerData.name && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg text-left">
              <div className="flex items-center">
                {workerData.profilePhotoUrl && (
            <img
              src={workerData.profilePhotoUrl}
              alt="Profile"
              className="w-16 h-16 rounded-full object-fill cursor-pointer border border-gray-300 hover:opacity-80 transition-opacity mr-4"
              onClick={(e) => {
                e.stopPropagation();
                handlePhotoClick(workerData.profilePhotoUrl);
              }}
            />
                )}
                <div>
            <h3 className="text-xl font-semibold">Name : {workerData.name}</h3>
            <p className="text-xl font-semibold">Phone No. : {workerData.phone}</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="w-full max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-white shadow-md">
                <tr className="bg-gray-100">
            <th className="p-2 text-center">Entry Date & Time</th>
            <th className="p-2 text-center">Exit Date & Time</th>
            <th className="p-2 text-center">Working Hours</th>
            <th className="p-2 text-center">Rating</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
            <tr>
              <td colSpan="4" className="text-center p-4">
                <Loader2 className="h-6 w-6 animate-spin mx-auto" />
              </td>
            </tr>
                ) : ratingHistory.length > 0 ? (
            ratingHistory.map((record, index) => (
              <tr key={record.id || index} className="border-b hover:bg-gray-50">
                <td className="p-2 text-center"> {formatDateTime(record.entryDateTime, true, record.busEntry, false, false)}</td>
                <td className="p-2 text-center"> {formatDateTime(record.exitDateTime, true, false, record.busExit, record.haveFood)}</td>
                <td className="p-2 text-center">
                  {record.workingHours !== null && record.workingHours !== undefined 
              ? `${record.workingHours} hrs` 
              : '-'}
                </td>
                <td className="p-2 text-center">
                  <div className="flex items-center justify-center flex-wrap gap-1">
              {record.rating ? (
                <div className="flex gap-0.5 mr-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                key={star}
                size={16}
                fill={star <= record.rating ? "#FFD700" : "none"}
                color={star <= record.rating ? "#FFD700" : "#D1D5DB"}
                    />
                  ))}
                </div>
              ) : (
                <button
                  onClick={(e) => {
                              e.stopPropagation();
                              openReviewModal(record.id, false);
                            }}
                            className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 mr-1"
                          >
                            Add Rating
                          </button>
                        )}
                        
                        {record.comments && record.comments.length > 0 && (
                          <Info 
                            size={16} 
                            className="text-blue-500 cursor-pointer" 
                            onMouseEnter={(e) => handleInfoHover(e, record.comments)}
                            onMouseLeave={handleInfoLeave}
                            onClick={(e) => handleInfoClick(e, record.comments)}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center p-4 text-gray-500">
                    No rating history found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tooltip */}
      {tooltipVisible && tooltipContent && (
        <div
          ref={tooltipRef}
          className={`fixed z-[9999] p-3 text-xs bg-white shadow-xl rounded-lg border border-gray-200 max-w-[200px] transition-opacity duration-200 ${
            isTooltipPinned ? 'pointer-events-auto' : 'pointer-events-none'
          }`}
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: 'translate(-50%, -100%)'
          }}
        >
          <div className="break-words">
            <div className="mt-0">
              {tooltipContent.map((item, index) => (
                <div key={index} className="text-gray-600 mb-1">
                  <span className="font-semibold">{item.key}:</span> {item.value}
                </div>
              ))}
            </div>
          </div>
        
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-white"></div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-200 mt-[-1px]"></div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium mb-4">
              {reviewData.isEditing ? "Update Worker Rating" : "Add Worker Rating"}
            </h3>
            
            <div className="flex justify-center mb-6">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  size={32}
                  className="cursor-pointer"
                  onClick={() => handleStarClick(star)}
                  fill={star <= reviewData.stars ? "#FFD700" : "none"}
                  color={star <= reviewData.stars ? "#FFD700" : "#D1D5DB"}
                />
              ))}
            </div>
            
            {reviewData.stars > 0 && reviewData.stars <= 3 && (
              <div className="mb-4 space-y-3">
                <div className="grid grid-cols-6 items-center">
                  <label className="col-span-2 text-sm font-medium text-gray-700">
                    Discipline:
                  </label>
                  <input
                    type="text"
                    value={reviewData.discipline}
                    onChange={(e) => handleInputChange(e, 'discipline')}
                    className="col-span-4 px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Enter discipline feedback"
                  />
                </div>
                
                <div className="grid grid-cols-6 items-center">
                  <label className="col-span-2 text-sm font-medium text-gray-700">
                    Quality of Work:
                  </label>
                  <input
                    type="text"
                    value={reviewData.qualityOfWork}
                    onChange={(e) => handleInputChange(e, 'qualityOfWork')}
                    className="col-span-4 px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Enter quality feedback"
                  />
                </div>
                
                <div className="grid grid-cols-6 items-center">
                  <label className="col-span-2 text-sm font-medium text-gray-700">
                    Behaviour:
                  </label>
                  <input
                    type="text"
                    value={reviewData.behaviour}
                    onChange={(e) => handleInputChange(e, 'behaviour')}
                    className="col-span-4 px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Enter behaviour feedback"
                  />
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowReviewModal(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={submitReview}
                disabled={reviewData.stars === 0 || (reviewData.stars <= 3 && !reviewData.discipline && !reviewData.qualityOfWork && !reviewData.behaviour)}
                className="px-4 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed"
              >
                {reviewData.isEditing ? "Update" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPhotoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[1000] p-4">
          <div className="relative max-w-md">
            <button
              onClick={closePhotoModal}
              className="absolute -top-4 -right-4 bg-white rounded-full p-2 hover:bg-gray-100 z-10"
            >
              <X size={24} className="text-gray-600" />
            </button>
            <div className="relative">
              <img
                src={selectedPhoto}
                alt="Profile"
                className={`w-full h-auto rounded-lg max-h-[80vh] object-fill`}
              />
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 text-center">
            <div className="text-green-500 mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium">
              {reviewData.isEditing ? "Rating updated successfully!" : "Rating recorded successfully!"}
            </h3>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="text-red-500 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-center mb-4">Error</h3>
            <p className="text-gray-600 text-center mb-6">{errorMessage}</p>
            <div className="flex justify-center">
              <button
                onClick={() => setShowErrorModal(false)}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperVisorRating;